import { z } from 'zod';

/**
 * Canonical normalized API error contract (SPEC-001 REQ-010/REQ-011).
 * Shared between server error normalization and the frontend API client.
 */
export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    recoverable: z.boolean(),
    requestId: z.string(),
  }),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export type ErrorCode =
  | 'INTERNAL_ERROR'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'AUTH_INVALID_CREDENTIALS'
  | 'AUTH_RATE_LIMITED'
  | 'AUTH_UNAUTHORIZED'
  | 'AUTH_SESSION_EXPIRED'
  | 'AUTH_USER_DISABLED'
  | 'PROFILE_VALIDATION_ERROR'
  | 'ATTACHMENT_STORAGE_ERROR'
  | 'FUNNEL_SAVE_VALIDATION_ERROR'
  | 'FUNNEL_SAVE_UNAUTHORIZED'
  | 'FUNNEL_SAVE_NOT_FOUND'
  | 'FUNNEL_REVISION_CONFLICT'
  | 'FUNNEL_SAVE_TOO_LARGE'
  | 'FUNNEL_SAVE_UNAVAILABLE'
  | 'IMPORT_INVALID_JSON'
  | 'IMPORT_FILE_TOO_LARGE'
  | 'IMPORT_UNSUPPORTED_FORMAT'
  | 'IMPORT_UNSUPPORTED_FORMAT_VERSION'
  | 'IMPORT_UNSUPPORTED_SCHEMA'
  | 'IMPORT_INVALID_GRAPH'
  | 'IMPORT_DANGLING_EDGE'
  | 'IMPORT_DUPLICATE_ID'
  | 'IMPORT_CREATE_FAILED'
  | 'VERSION_NOT_FOUND'
  | 'VERSION_UNSUPPORTED_SCHEMA'
  | 'VERSION_RESTORE_CONFLICT'
  | 'VERSION_SNAPSHOT_FAILED'
  | 'TRASH_NOT_FOUND'
  | 'TRASH_RESTORE_FAILED'
  | 'TRASH_PERMANENT_DELETE_FAILED'
  | 'AUDIT_WRITE_FAILED'
  | 'AI_UNAVAILABLE'
  | 'AI_TIMEOUT'
  | 'AI_RATE_LIMITED'
  | 'AI_QUOTA_EXCEEDED'
  | 'AI_INVALID_RESPONSE'
  | 'AI_CONTEXT_TOO_LARGE'
  | 'AI_OPERATION_UNSUPPORTED';

export function buildApiError(
  code: ErrorCode,
  message: string,
  requestId: string,
  recoverable = false,
): ApiError {
  return { error: { code, message, recoverable, requestId } };
}
